function clean(value) {
  return String(value ?? "").replace(/\s+/g, " ").trim();
}

function clip(value, max) {
  const text = clean(value);
  if (text.length <= max) return text;
  return `${text.slice(0, max - 3).trim()}...`;
}

export function formatScoreNumber(value) {
  if (!Number.isFinite(Number(value))) return "-";
  const n = Number(value);
  if (Number.isInteger(n)) return String(n);
  return String(Math.round(n * 100) / 100);
}

export function formatPercent(awarded, max) {
  const total = Number(max);
  const score = Number(awarded);
  if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(score)) return "0%";
  const pct = Math.round((score / total) * 1000) / 10;
  return Number.isInteger(pct) ? `${pct}%` : `${pct.toFixed(1)}%`;
}

function toneOf(question) {
  const awarded = Number(question?.marks_awarded);
  const max = Number(question?.max_marks);
  const status = String(question?.status || "").toLowerCase();
  if (Number.isFinite(awarded) && Number.isFinite(max) && max > 0) {
    if (awarded >= max) return "correct";
    if (awarded <= 0) return "incorrect";
    return "partial";
  }
  if (status === "correct") return "correct";
  if (status === "incorrect") return "incorrect";
  return "partial";
}

function questionNumber(id) {
  const match = String(id || "").match(/\d+/);
  return match ? Number(match[0]) : Number.POSITIVE_INFINITY;
}

export function formatQuestionList(questions) {
  const items = (questions || [])
    .map((question) => ({
      label: clean(question?.id) || "Q",
      n: questionNumber(question?.id),
    }))
    .sort((a, b) => a.n - b.n || a.label.localeCompare(b.label));
  if (!items.length) return "";
  if (items.some((item) => !Number.isFinite(item.n))) {
    return items.map((item) => item.label).join(", ");
  }

  const parts = [];
  let index = 0;
  while (index < items.length) {
    let end = index;
    while (end + 1 < items.length && items[end + 1].n === items[end].n + 1) end += 1;
    parts.push(end === index ? items[index].label : `${items[index].label}-${items[end].label}`);
    index = end + 1;
  }
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

function componentEarned(item) {
  const awarded = Number(item?.marks_awarded ?? item?.mark ?? item?.awarded);
  const max = Number(item?.max_marks ?? item?.max);
  if (Number.isFinite(awarded) && Number.isFinite(max) && max > 0) return awarded >= max;
  if (Number.isFinite(awarded)) return awarded > 0;
  const status = clean(item?.status).toLowerCase();
  return status === "correct" || status === "awarded";
}

export function questionMarkLines(question) {
  const components = Array.isArray(question?.components) ? question.components : [];
  const lines = [];
  components.forEach((item) => {
    if (!item || typeof item !== "object") return;
    const name = clean(item.name || item.label || item.code);
    const note = clean(item.comment || item.feedback || item.description || item.reason);
    if (!name && !note) return;
    const earned = componentEarned(item);
    const codeLike = /^[A-Za-z]\d{0,2}$/.test(name);
    if (codeLike) {
      lines.push({
        code: name.toUpperCase(),
        earned,
        label: note || (earned ? "Awarded" : "Not awarded"),
      });
      return;
    }
    lines.push({
      code: "",
      earned,
      label: note ? `${name}: ${note}` : name,
    });
  });
  if (lines.length) return lines.slice(0, 5).map((line) => ({ ...line, label: clip(line.label, 90) }));

  const tone = toneOf(question);
  const feedback = clip(question?.feedback, 90);
  if (tone === "correct") {
    return [{ code: "", earned: true, label: feedback || "Correct answer" }];
  }
  if (tone === "incorrect") {
    return [{ code: "", earned: false, label: feedback || "No marks awarded" }];
  }
  return [{ code: "", earned: false, label: feedback || "Partial credit" }];
}

export function pageMarkSummary(questions) {
  const list = Array.isArray(questions) ? questions : [];
  let awarded = 0;
  let max = 0;
  list.forEach((question) => {
    awarded += Number(question?.marks_awarded) || 0;
    max += Number(question?.max_marks) || 0;
  });
  const missed = list.filter((question) => toneOf(question) !== "correct");
  const allCorrect = list.length > 0 && missed.length === 0;
  let headline = "No questions were marked on this page.";
  let detail = "";
  if (list.length && allCorrect) {
    headline = "Excellent - all work on this page is correct.";
    detail =
      list.length === 1
        ? "This question was awarded full marks."
        : `Good accuracy across ${list.length} questions.`;
  } else if (list.length && missed.length === list.length) {
    headline =
      list.length === 1
        ? "This question was not awarded full marks."
        : "Marks were lost on every question on this page.";
    detail = clip(missed.find((question) => clean(question?.feedback))?.feedback, 180);
  } else if (list.length) {
    headline = `${missed.length} ${missed.length === 1 ? "question needs" : "questions need"} review on this page.`;
    detail = clip(
      missed
        .map((question) => {
          const note = clean(question?.feedback);
          const id = clean(question?.id);
          return note ? `${id || "Question"}: ${note}` : id;
        })
        .filter(Boolean)
        .join(" "),
      180
    );
  }

  return {
    awarded,
    max,
    percentLabel: formatPercent(awarded, max),
    scoreLabel: `${formatScoreNumber(awarded)} / ${formatScoreNumber(max)}`,
    headline,
    detail,
    hasQuestions: list.length > 0,
    allCorrect,
  };
}

export function buildExamReport({
  questions,
  examName,
  studentName,
  awarded,
  max,
  hideScore,
  overallComment,
} = {}) {
  const sorted = (Array.isArray(questions) ? questions : [])
    .filter(Boolean)
    .slice()
    .sort((a, b) => questionNumber(a?.id) - questionNumber(b?.id) || clean(a?.id).localeCompare(clean(b?.id)));
  const full = sorted.filter((question) => toneOf(question) === "correct");
  const missed = sorted.filter((question) => toneOf(question) !== "correct");
  const summedAwarded = sorted.reduce((sum, question) => sum + (Number(question?.marks_awarded) || 0), 0);
  const summedMax = sorted.reduce((sum, question) => sum + (Number(question?.max_marks) || 0), 0);
  const totalAwarded = Number.isFinite(Number(awarded)) ? Number(awarded) : summedAwarded;
  const totalMax = Number.isFinite(Number(max)) ? Number(max) : summedMax;
  const hide = Boolean(hideScore);
  const lost = Math.max(0, totalMax - totalAwarded);

  const areas = [];
  sorted.forEach((question) => {
    (Array.isArray(question?.areas_of_improvement) ? question.areas_of_improvement : []).forEach((area) => {
      const text = clean(area);
      if (text && !areas.includes(text)) areas.push(text);
    });
  });

  const fullNames = formatQuestionList(full);
  const strengths = full.length
    ? `Strong and consistent work on the questions that were awarded full marks. ${fullNames} ${full.length === 1 ? "is" : "are"} fully correct.`
    : "No questions were awarded full marks. Use the comments on each question to see what to practise next.";

  let errors = "No errors to review. Every marked question was awarded full marks.";
  if (missed.length) {
    const blanks = missed.filter((question) => !(Number(question?.marks_awarded) > 0));
    const named = formatQuestionList(blanks.length === missed.length ? blanks : missed);
    const reason =
      blanks.length === missed.length
        ? `${named} ${missed.length === 1 ? "was" : "were"} left without marks.`
        : `${named} ${missed.length === 1 ? "was" : "were"} not awarded full marks.`;
    if (!hide && lost > 0) {
      const account = missed.length === 1 ? "This accounts" : "These account";
      errors = `${reason} ${account} for all ${formatScoreNumber(lost)} lost ${lost === 1 ? "mark" : "marks"}.`;
    } else {
      errors = reason;
    }
  }

  let priority = "Review the questions that lost marks and redo that working before the next paper.";
  if (areas.length) priority = areas[0];
  else if (!missed.length) priority = "Keep the same accuracy on the next paper, including every part of each question.";
  else if (missed.some((question) => !(Number(question?.marks_awarded) > 0))) {
    priority =
      "Complete every assigned question before submitting. The work that was attempted is the part to keep; the priority is avoiding unanswered questions.";
  } else if (clean(missed[0]?.feedback)) {
    priority = clean(missed[0].feedback);
  }

  const scope = sorted.length
    ? `This report marks all questions visibly present in the uploaded script: ${formatQuestionList(sorted)}.`
    : "This report covers the questions included in the marked script.";

  const comment = clean(overallComment);
  let examiner = comment;
  if (!examiner) {
    if (!missed.length) examiner = "Excellent accuracy. Every marked question was awarded full marks.";
    else if (missed.every((question) => !(Number(question?.marks_awarded) > 0)) && full.length) {
      examiner =
        "Excellent accuracy on the attempted work. The only score loss came from unanswered questions, not from errors in the attempted work.";
    } else {
      examiner = "Review the questions that lost marks. The rest of the attempted work shows a secure method.";
    }
  }

  const percent = totalMax > 0 ? (totalAwarded / totalMax) * 100 : 0;

  return {
    examName: clean(examName) || "Exam",
    studentName: clean(studentName),
    tagline: "Marked  |  Reviewed  |  Returned",
    hideScore: hide,
    totalAwarded,
    totalMax,
    percent,
    percentLabel: formatPercent(totalAwarded, totalMax),
    scoreLabel: `${formatScoreNumber(totalAwarded)} / ${formatScoreNumber(totalMax)}`,
    questionsMarked: sorted.length,
    fullMarkCount: full.length,
    chips: sorted.map((question) => ({
      id: clean(question?.id) || "Q",
      awarded: Number.isFinite(Number(question?.marks_awarded)) ? Number(question.marks_awarded) : 0,
      max: Number.isFinite(Number(question?.max_marks)) ? Number(question.max_marks) : 0,
      tone: toneOf(question),
    })),
    strengths,
    errors,
    priority,
    scope,
    examiner,
    consistent: hide
      ? `Consistency check passed across ${sorted.length} marked ${sorted.length === 1 ? "question" : "questions"}.`
      : `Consistency check passed question totals sum to ${formatScoreNumber(totalAwarded)}/${formatScoreNumber(totalMax)}.`,
    praise: !missed.length || percent >= 80 ? "Well done!" : "Keep going",
  };
}
